import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatClockTime, getTimeFormat, useTimeFormat, type TimeFormat } from '@/shared/lib/time-format';

/**
 * Live clock + timezone offset for a calendar view's time-gutter header.
 *
 * One implementation for every calendar view (PROD-1797). Before this it was
 * three different things:
 *
 *   - `day-list-calendar.tsx`  — computed the offset from the browser (correct)
 *   - `day-calendar.tsx` / `week-calendar.tsx` — hardcoded the string `GMT+04`
 *   - `by-room-calendar.tsx` / `by-doctor-calendar.tsx` — nothing at all
 *
 * The client reported the third case (Room and Doctor). The second is the more
 * dangerous one: `GMT+04` is a literal, so any deployment outside the UAE — or a
 * user travelling — reads a confidently wrong timezone off the schedule.
 *
 * ## Why the offset text is load-bearing here
 *
 * Not decoration. Timezone handling in this codebase has produced repeated
 * confirmed defects: appointments stored 4 hours early (PROD-1752), every report
 * date filter off by a day (PROD-1763), an org licence expiry shipped as a
 * `Z`-suffixed instant (PROD-1773). Staff reading a schedule benefit from the
 * grid stating which zone it is drawn in, and Room/Doctor are precisely the views
 * used for cross-provider and cross-room coordination.
 *
 * Offset is derived from `Date.getTimezoneOffset()` — the same browser zone the
 * grid itself is laid out in — so the label can never disagree with the rows
 * beside it. That is the reason it is computed rather than read from user
 * settings: a label sourced independently of the layout can drift from it.
 *
 * A user opening the calendar from another timezone therefore sees *their* zone,
 * not the org's. That is correct, not a gap: the rows are positioned in their zone
 * too, so the label describes the grid they are actually looking at. Switching
 * this to org config without also re-projecting every row would make the label
 * lie — please don't. (Client-only by construction; nothing here runs in SSR.)
 *
 * Handles sub-hour zones (`GMT+5:30`), which a hardcoded string cannot.
 *
 * ## Deliberately not configurable
 *
 * There is no `offset` or `timezone` prop, and there should not be one. The whole
 * point is that the label and the grid read the same clock; a caller-supplied
 * offset would let the two disagree, which is the failure this component exists
 * to prevent. `className` styles the gutter box only — it cannot change what the
 * label says.
 *
 * ## Centering is built in, not passed in
 *
 * The two lines are centered by the component itself. Before extraction, the
 * day-list version returned a *fragment*, so its lines were direct children of a
 * centering flex parent; wrapping them in a `div` silently left-aligned them.
 * Making the wrapper own the centering restores day-list and makes the four other
 * gutters agree with it, rather than leaving each caller to remember.
 */
export function GutterClock({ className, ...pin }: { className?: string; /** The grid's sticky corner (showcase-bridge.css). */ 'data-cal-gutter'?: boolean }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // 60s is enough for a minute-resolution label; the interval is cleared on
    // unmount so switching views does not leak timers.
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Subscribed, so a change in Settings › Account details › Display re-renders the clock at once, not at the next tick.
  const format = useTimeFormat();

  // Two meta lines (16 + 16) fit the 40-tall header row inside its 4 / 8 padding. The live time is the danger red
  // (PROD-1797, as before the reskin); the offset stays the hint grey.
  return (
    <div {...pin} className={cn('flex flex-col items-center justify-center', className)}>
      <div
        data-gutter-clock-now
        className="text-[length:var(--type-meta-size)] font-medium leading-[var(--type-meta-line)] tabular-nums text-status-error"
      >
        {formatClockLabel(now, format)}
      </div>
      <div className="text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-hint">{formatGmtOffset(now)}</div>
    </div>
  );
}

/** `1:34p` (12h, the default) or `13:34` (24h) — the current time format, matching the hour gutter. */
export function formatClockLabel(now: Date, format: TimeFormat = getTimeFormat()): string {
  return formatClockTime(now, format);
}

/**
 * `GMT+4`, `GMT-5`, `GMT+5:30`.
 *
 * `getTimezoneOffset()` returns minutes WEST of UTC, so it is negated: Dubai
 * reports `-240` and must read `GMT+4`. Getting that backwards is the classic
 * error, and it would render every schedule in the wrong direction.
 */
export function formatGmtOffset(now: Date): string {
  const tzMin = -now.getTimezoneOffset();
  const tzH = Math.trunc(tzMin / 60);
  const tzExtra = Math.abs(tzMin) % 60;
  const sign = tzMin >= 0 ? '+' : '-';
  return tzExtra === 0
    ? `GMT${sign}${Math.abs(tzH)}`
    : `GMT${sign}${Math.abs(tzH)}:${String(tzExtra).padStart(2, '0')}`;
}
